import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { DeviceInfo } from '@capacitor/device/dist/esm/definitions';
import { Preferences } from '@capacitor/preferences';
import { isCordova } from 'utils/platform';
import { create } from 'zustand';

const ANONYMOUS_DEVICE_ID_KEY = 'anonymousDeviceId';

async function fetchOrCreateAnonymousId(): Promise<string> {
  try {
    const stored = await Preferences.get({ key: ANONYMOUS_DEVICE_ID_KEY });
    if (stored.value) {
      return stored.value;
    }
  } catch (error) {
    console.error('Failed to get anonymousDeviceId from Preferences:', error);
  }

  const newId = crypto.randomUUID();
  try {
    await Preferences.set({ key: ANONYMOUS_DEVICE_ID_KEY, value: newId });
  } catch (error) {
    console.error('Failed to persist anonymousDeviceId:', error);
  }
  return newId;
}

let anonymousIdPromise: Promise<string> | null = null;

function getAnonymousIdOnce(): Promise<string> {
  if (!anonymousIdPromise) {
    anonymousIdPromise = fetchOrCreateAnonymousId();
  }
  return anonymousIdPromise;
}

type DeviceInfoState = {
  deviceInfo: DeviceInfo | null;
  anonymousDeviceId: string | undefined;
  init: () => Promise<void>;
  setDeviceInfo: (deviceInfo: DeviceInfo) => void;
  getOrCreateAnonymousDeviceId: () => Promise<string>;
};

export const useDeviceInfoStore = create<DeviceInfoState>()((set) => ({
  deviceInfo: null,
  anonymousDeviceId: undefined,
  init: async () => {
    if (!isCordova()) {
      return;
    }

    const anonymousDeviceId = await getAnonymousIdOnce();
    set({ anonymousDeviceId });

    if (!Capacitor.isPluginAvailable('Device')) {
      return;
    }
    try {
      const deviceInfo = await Device.getInfo();
      set({ deviceInfo });
    } catch (error) {
      console.error('Failed to get device info:', error);
    }
  },
  setDeviceInfo: (deviceInfo) => {
    set({ deviceInfo });
  },
  getOrCreateAnonymousDeviceId: getAnonymousIdOnce,
}));
